import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScreenData } from "../../types/ScreenTypes";
import { fetchEventsICS, hydrateEvents } from "../../services/eventsService";
import { mixWithWhite, readableColour } from "../../utils/colourContrast";
import useLabbaikItems, { labbaikEvents } from "../../hooks/useLabbaikItems";
import "./UpcomingEventsWidget.css";

interface UpcomingEventsWidgetProps {
  masjidId: number;
  duration: number;
  onComplete: () => void;
  screenData: ScreenData;
  // Custom colors for theming (uses same 3-color system as salah changes)
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  // Progress tracking props
  currentIndex?: number;
  totalItems?: number;
  elapsedTime?: number;
  totalDuration?: number;
  // Restart trigger to reset the component without unmounting
  restartTrigger?: number;
  // Behavior controls
  hideWhenNoEvents?: boolean;
  onNoEvents?: () => void;
}

interface ParsedEvent {
  uid: string;
  summary: string;
  description?: string;
  location?: string;
  speakers?: string;
  audience?: string;
  start: Date;
  end?: Date;
  allDay?: boolean;
  timeLabel?: string;
}

interface GroupedEvents {
  date: string;
  displayDate: string;
  dayName: string;
  isToday: boolean;
  isTomorrow: boolean;
  events: ParsedEvent[];
}

const UpcomingEventsWidget: React.FC<UpcomingEventsWidgetProps> = ({
  masjidId,
  duration,
  onComplete,
  screenData,
  primaryColor = "#F5A623", // Warm Amber/Gold
  secondaryColor = "#FF9800", // Default orange
  accentColor = "#4CAF50", // Default green
  currentIndex,
  totalItems,
  elapsedTime,
  totalDuration,
  restartTrigger = 0,
  hideWhenNoEvents = false,
  onNoEvents,
}) => {
  // Timer management
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef<boolean>(false);
  const onCompleteCallbackRef = useRef(onComplete);
  const noEventsHandledRef = useRef(false);

  // State
  const [events, setEvents] = useState<ParsedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const labbaikItems = useLabbaikItems(masjidId, screenData.showLabbaikEvents ?? screenData.showLabbaik);
  const allEvents = useMemo(
    () => [...events, ...labbaikEvents(labbaikItems, new Date())],
    [events, labbaikItems],
  );

  // Update the callback ref when onComplete changes
  useEffect(() => {
    onCompleteCallbackRef.current = onComplete;
  }, [onComplete]);

  // Reset when restartTrigger changes
  useEffect(() => {
    hasCompletedRef.current = false;
    noEventsHandledRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [restartTrigger]);

  // Set up the auto-advance timer
  useEffect(() => {
    if (hasCompletedRef.current) return;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set up new timer for duration
    timerRef.current = setTimeout(() => {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onCompleteCallbackRef.current?.();
      }
    }, duration * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [duration, restartTrigger]);

  // Fetch events using the events service (with offline caching and deduplication)
  useEffect(() => {
    async function loadEvents() {
      setLoading(true);
      setError(null);

      try {
        // Use timesRefreshInterval from screenData (default 300 seconds / 5 minutes)
        const refreshInterval = screenData.timesRefreshInterval || 300;
        const storedEvents = await fetchEventsICS(masjidId, refreshInterval);

        // Hydrate stored events (convert ISO strings back to Date objects)
        const hydratedEvents = hydrateEvents(storedEvents);
        setEvents(hydratedEvents);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err instanceof Error ? err.message : "Failed to load events");
        setEvents([]);
      } finally {
        setLoading(false);
      }
    }

    const handleEventsUpdated = () => {
      loadEvents();
    };

    if (masjidId) {
      loadEvents(); // Initial load
    }

    window.addEventListener("events-updated", handleEventsUpdated);
    return () =>
      window.removeEventListener("events-updated", handleEventsUpdated);
  }, [masjidId, screenData.timesRefreshInterval]);

  // Group events by day for the next 7 days
  const groupedEvents = useMemo<GroupedEvents[]>(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const groups: GroupedEvents[] = [];

    for (let i = 0; i < 7; i++) {
      const day = new Date(today);
      day.setDate(today.getDate() + i);
      const dayEnd = new Date(day);
      dayEnd.setDate(day.getDate() + 1);

      const dayKey = day.toISOString().split("T")[0];

      // Filter events for this day
      const dayEvents = allEvents.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate >= day && eventDate < dayEnd;
      });

      // For today, only show events that haven't finished yet (future or ongoing)
      const filteredEvents =
        i === 0
          ? dayEvents.filter((event) => {
              if (event.allDay) return true;
              // Show if event hasn't started yet OR is currently ongoing
              return event.start >= now || (event.end && event.end > now);
            })
          : dayEvents;

      groups.push({
        date: dayKey,
        displayDate: day.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        dayName: day.toLocaleDateString(undefined, { weekday: "long" }),
        isToday: i === 0,
        isTomorrow: i === 1,
        events: filteredEvents,
      });
    }

    return groups;
  }, [allEvents]);

  // Total events count for display
  const totalEventsCount = useMemo(() => {
    return groupedEvents.reduce((sum, g) => sum + g.events.length, 0);
  }, [groupedEvents]);

  useEffect(() => {
    if (
      hideWhenNoEvents &&
      !loading &&
      totalEventsCount === 0 &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      if (!noEventsHandledRef.current) {
        noEventsHandledRef.current = true;
        onNoEvents?.();
      }
      onCompleteCallbackRef.current?.();
    }
  }, [hideWhenNoEvents, loading, totalEventsCount, onNoEvents]);

  // Use colors from screenData if available, otherwise use props
  const resolvedPrimaryColor = screenData.primaryColor || primaryColor;
  const resolvedSecondaryColor = screenData.secondaryColor || secondaryColor;
  const resolvedAccentColor = screenData.accentColor || accentColor;

  const resolvedBackground = screenData.widgetBackgroundColor || "#0a0a0a";
  // This widget's cards are a 5% white wash over its background
  const cardColour = mixWithWhite(resolvedBackground, 0.05);

  // CSS custom properties for theming
  const customStyles = {
    "--events-primary-color": resolvedPrimaryColor,
    "--events-secondary-color": resolvedSecondaryColor,
    "--events-accent-color": resolvedAccentColor,
    "--events-background-color": resolvedBackground,
    "--events-time-color": readableColour([resolvedAccentColor, resolvedSecondaryColor, resolvedPrimaryColor], cardColour),
    "--events-soon-color": readableColour([resolvedSecondaryColor, resolvedPrimaryColor, resolvedAccentColor], cardColour),
    "--heading-text-color": screenData.headingTextColor || "#FFFFFF",
  } as React.CSSProperties;

  // Format time for display
  const formatTime = (date: Date, allDay?: boolean): string => {
    if (allDay) return "All Day";

    const use12Hour = screenData.use12HourFormat !== false;
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: use12Hour,
    });
  };

  if (loading) {
    return (
      <div className="upcoming-events-widget" style={customStyles}>
        <div className="events-widget-header">
          <h2>Upcoming Events</h2>
        </div>
        <div className="events-widget-content">
          <div className="events-loading">
            <div className="events-loading-spinner"></div>
            <p>Loading events...</p>
          </div>
        </div>
      </div>
    );
  }

  if (hideWhenNoEvents && totalEventsCount === 0) {
    return null;
  }

  return (
    <div className="upcoming-events-widget" style={customStyles}>
      <div className="events-widget-header">
        <h2>Upcoming Events</h2>
        {totalEventsCount > 0 && (
          <span className="events-count">
            {totalEventsCount} event{totalEventsCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="events-widget-content">
        {totalEventsCount === 0 ? (
          <div className="events-no-events">
            <p>No upcoming events</p>
            <span>Check back soon for announcements!</span>
          </div>
        ) : (
          <div className="events-list">
            {groupedEvents
              .filter((group) => group.events.length > 0) // Only show days with events
              .map((group) => (
                <div key={group.date} className="events-day-group">
                  <div
                    className={`events-day-header ${group.isToday ? "today" : ""} ${group.isTomorrow ? "tomorrow" : ""}`}
                  >
                    <span className="events-day-name">
                      {group.isToday
                        ? "Today"
                        : group.isTomorrow
                          ? "Tomorrow"
                          : group.dayName}
                    </span>
                    <span className="events-day-date">{group.displayDate}</span>
                  </div>

                  <div className="events-day-items">
                    {group.events
                      .slice() // Create copy to avoid mutating original
                      .sort((a, b) => {
                        // All-day events first (like iOS)
                        if (a.allDay && !b.allDay) return -1;
                        if (!a.allDay && b.allDay) return 1;
                        // Then by start time
                        return a.start.getTime() - b.start.getTime();
                      })
                      .map((event, idx) => {
                        const now = new Date();
                        const isOngoing =
                          !event.allDay &&
                          event.start <= now &&
                          event.end &&
                          event.end > now;
                        const isUpcoming =
                          !event.allDay &&
                          event.start > now &&
                          event.start.getTime() - now.getTime() <=
                            30 * 60 * 1000; // Within 30 min

                        return (
                          <div
                            key={`${event.uid}-${idx}`}
                            className={`events-item ${isOngoing ? "ongoing" : ""} ${isUpcoming ? "upcoming-soon" : ""}`}
                          >
                            <div className="events-item-accent"></div>
                            <div className="events-item-time">
                              {isOngoing
                                ? "Now"
                                : event.timeLabel || formatTime(event.start, event.allDay)}
                            </div>
                            <div className="events-item-details">
                              <div className="events-item-title">
                                {event.summary}
                              </div>
                              {event.audience && (
                                <div className="events-item-audience">
                                  👥 {event.audience}
                                </div>
                              )}
                              {event.location && (
                                <div className="events-item-location">
                                  📍 {event.location}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Progress indicator */}
      {typeof currentIndex === "number" && typeof totalItems === "number" && (
        <div className="events-widget-progress">
          <span className="events-progress-text">
            {currentIndex + 1} / {totalItems}
          </span>
        </div>
      )}
    </div>
  );
};

export default UpcomingEventsWidget;
